import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import type { UserApi as User } from '@/api';
import { authApi } from '@/api/service';
import { AUTH_CONFIG } from '@/config';
import { useCartStore } from '@/store/cartStore';
import { logger } from '@/utils/logger';
import { session, storage } from '@/utils/storage';

interface AuthError {
  code: string;
  message: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: AuthError | null;
  login: (email: string, password: string) => Promise<boolean>;
  register: (name: string, email: string, password: string) => Promise<boolean>;
  logout: () => void;
  forgotPassword: (email: string) => Promise<boolean>;
  resetPassword: (token: string, password: string) => Promise<boolean>;
  updateProfile: (data: Partial<User>) => Promise<boolean>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const saveTokens = (token: string, refreshToken: string) => {
  storage.set(AUTH_CONFIG.tokenKey, token);
  storage.set(AUTH_CONFIG.refreshTokenKey, refreshToken);
};

const clearTokens = () => {
  storage.remove(AUTH_CONFIG.tokenKey);
  storage.remove(AUTH_CONFIG.refreshTokenKey);
};

const getStoredToken = (): string | null => storage.get<string>(AUTH_CONFIG.tokenKey);

const getStoredRefreshToken = (): string | null => storage.get<string>(AUTH_CONFIG.refreshTokenKey);

const getLoginAttempts = (): number => {
  const attempts = session.get<number>('login_attempts');
  return attempts || 0;
};

const incrementLoginAttempts = (): void => {
  const attempts = getLoginAttempts() + 1;
  session.set('login_attempts', attempts);
  session.set('last_attempt_time', Date.now());
};

const resetLoginAttempts = (): void => {
  session.remove('login_attempts');
  session.remove('last_attempt_time');
};

const isAccountLocked = (): boolean => {
  const attempts = getLoginAttempts();
  if (attempts >= AUTH_CONFIG.maxLoginAttempts) {
    const lastAttempt = session.get<number>('last_attempt_time') || 0;
    const elapsed = Date.now() - lastAttempt;
    return elapsed < AUTH_CONFIG.lockoutDuration;
  }
  return false;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = getStoredToken();

        if (token) {
          const response = await authApi.getCurrentUser();
          if (response.success && response.data) {
            setUser(response.data);
          } else if (AUTH_CONFIG.enablePersistentSession) {
            const refreshToken = getStoredRefreshToken();
            if (refreshToken) {
              try {
                const refreshResponse = await authApi.refreshToken(refreshToken);
                if (refreshResponse.success && refreshResponse.data) {
                  saveTokens(refreshResponse.data.token, refreshResponse.data.refreshToken);
                  const retryResponse = await authApi.getCurrentUser();
                  if (retryResponse.success && retryResponse.data) {
                    setUser(retryResponse.data);
                    return;
                  }
                }
              } catch {
                // Refresh failed, fall through to clear
              }
            }
            clearTokens();
            setUser(null);
          } else {
            clearTokens();
            setUser(null);
          }
        }
      } catch (err) {
        logger.error('Failed to restore auth session', { error: err });
        clearTokens();
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    void initAuth();
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const login = useCallback(
    async (email: string, password: string): Promise<boolean> => {
      setIsLoading(true);
      clearError();

      try {
        if (isAccountLocked()) {
          setError({
            code: 'ACCOUNT_LOCKED',
            message: 'Too many failed attempts. Please try again later.',
          });
          logger.warn('Login attempt while account locked', { email });
          return false;
        }

        if (!email?.trim() || !password?.trim()) {
          setError({
            code: 'INVALID_INPUT',
            message: 'Please provide both email and password.',
          });
          return false;
        }

        const response = await authApi.login({ email, password });

        if (!response.success || !response.data) {
          incrementLoginAttempts();
          setError({
            code: response.error?.code || 'LOGIN_FAILED',
            message: response.error?.message || 'Invalid email or password.',
          });
          return false;
        }

        const { user: userData, token, refreshToken } = response.data;
        setUser(userData);
        saveTokens(token, refreshToken);
        resetLoginAttempts();

        void useCartStore.getState().mergeGuestCart();

        logger.info('User logged in successfully', { userId: userData.id });
        return true;
      } catch (err) {
        incrementLoginAttempts();
        const errorMessage = err instanceof Error ? err.message : 'Login failed';
        setError({
          code: 'LOGIN_FAILED',
          message: errorMessage,
        });
        logger.error('Login failed', { error: err, email });
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [clearError]
  );

  const register = useCallback(
    async (name: string, email: string, password: string): Promise<boolean> => {
      setIsLoading(true);
      clearError();

      try {
        if (!name?.trim() || !email?.trim() || !password?.trim()) {
          setError({
            code: 'INVALID_INPUT',
            message: 'All fields are required.',
          });
          return false;
        }

        const response = await authApi.register({
          name,
          email,
          password,
          confirmPassword: password,
        });

        if (!response.success || !response.data) {
          setError({
            code: response.error?.code || 'REGISTRATION_FAILED',
            message: response.error?.message || 'Registration failed.',
          });
          return false;
        }

        const { user: userData, token, refreshToken } = response.data;
        setUser(userData);
        saveTokens(token, refreshToken);

        void useCartStore.getState().mergeGuestCart();

        logger.info('User registered successfully', { userId: userData.id });
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Registration failed';
        setError({
          code: 'REGISTRATION_FAILED',
          message: errorMessage,
        });
        logger.error('Registration failed', { error: err, email });
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [clearError]
  );

  const logout = useCallback(async () => {
    setUser(null);
    clearTokens();

    session.remove('login_attempts');
    session.remove('last_attempt_time');

    try {
      await authApi.logout();
    } catch (err) {
      logger.warn('Failed to logout on backend', { error: err });
    }

    logger.info('User logged out');
  }, []);

  const resetPassword = useCallback(
    async (token: string, newPassword: string): Promise<boolean> => {
      setIsLoading(true);
      clearError();

      try {
        const response = await authApi.resetPassword(token, newPassword);

        if (!response.success) {
          setError({
            code: response.error?.code || 'RESET_PASSWORD_FAILED',
            message: response.error?.message || 'Failed to reset password.',
          });
          return false;
        }

        logger.info('Password reset successfully');
        return true;
      } catch (err) {
        logger.error('Password reset failed', { error: err });
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [clearError]
  );

  const forgotPassword = useCallback(
    async (email: string): Promise<boolean> => {
      setIsLoading(true);
      clearError();

      try {
        if (!email?.trim()) {
          setError({
            code: 'INVALID_INPUT',
            message: 'Please provide your email address.',
          });
          return false;
        }

        const response = await authApi.forgotPassword(email);

        if (!response.success) {
          setError({
            code: response.error?.code || 'FORGOT_PASSWORD_FAILED',
            message: response.error?.message || 'Failed to request password reset.',
          });
          return false;
        }

        logger.info('Password reset requested', { email: email.toLowerCase() });
        return true;
      } catch (err) {
        logger.error('Password reset request failed', { error: err, email });
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [clearError]
  );

  const updateProfile = useCallback(
    async (data: Partial<User>): Promise<boolean> => {
      setIsLoading(true);
      clearError();

      try {
        if (!user) {
          setError({
            code: 'NOT_AUTHENTICATED',
            message: 'You must be logged in to update your profile.',
          });
          return false;
        }

        const response = await authApi.updateProfile(data);

        if (!response.success || !response.data) {
          setError({
            code: response.error?.code || 'UPDATE_FAILED',
            message: response.error?.message || 'Profile update failed.',
          });
          return false;
        }

        const updatedUser = response.data;
        setUser(updatedUser);

        logger.info('Profile updated', { userId: user.id });
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Profile update failed';
        setError({
          code: 'UPDATE_FAILED',
          message: errorMessage,
        });
        logger.error('Profile update failed', { error: err, userId: user?.id });
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [clearError, user]
  );

  const contextValue = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      error,
      login,
      register,
      logout,
      forgotPassword,
      resetPassword,
      updateProfile,
      clearError,
    }),
    [
      user,
      isLoading,
      error,
      login,
      register,
      logout,
      forgotPassword,
      resetPassword,
      updateProfile,
      clearError,
    ]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export type { AuthContextType, AuthError, User };
